import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { MultiPolygon, Polygon } from "geojson";
import { MapDataEvent } from "mapbox-gl";
import { useThrottledCallback } from "use-debounce";

import { FeatureTotals } from "fond/map/featureTotalsSummary";
import { countLayerConfigFeatures, EMPTY_MAPBOX_LAYER_FEATURES, readMapboxLayerFeatures } from "fond/map/mapboxFeatureTotals";
import { MapContext } from "fond/map/MapProvider";
import { Configuration, Layer } from "fond/types";
import { LayerConfig, SublayerConfig } from "fond/types/ProjectLayerConfig";

interface UseMapboxFeatureTotalsProps {
  versionConfig: Configuration | undefined;
  /** Layers that exist in the backend, keyed by configuration ID. */
  realLayers: Record<string, Layer>;
  layerConfigs: Array<LayerConfig | SublayerConfig>;
  selectionGeometry?: Polygon | MultiPolygon | null;
}

/**
 * Feature totals for the layers the map draws from its geojson sources.
 *
 * Reading the map is throttled, as its source data changes fairly frequently (e.g. when scrolling
 * the map). The totals themselves are derived, so a change of selection is reflected immediately.
 */
const useMapboxFeatureTotals = ({ versionConfig, realLayers, layerConfigs, selectionGeometry }: UseMapboxFeatureTotalsProps): FeatureTotals => {
  const { map } = useContext(MapContext);
  const [layerFeatures, setLayerFeatures] = useState(EMPTY_MAPBOX_LAYER_FEATURES);

  const readLayerFeatures = useThrottledCallback(
    useCallback(() => {
      const style = map?.getStyle();
      if (!style || !versionConfig) return;

      setLayerFeatures(readMapboxLayerFeatures(style, { versionConfig, realLayers }));
    }, [map, versionConfig, realLayers]),
    500
  );

  useEffect(() => {
    if (!map || !versionConfig) {
      setLayerFeatures(EMPTY_MAPBOX_LAYER_FEATURES);
      return undefined;
    }

    readLayerFeatures();

    const handleData = (e: MapDataEvent) => {
      if (e.dataType !== "source" || !e.isSourceLoaded || e.source?.type !== "geojson" || !e.sourceId) return;
      readLayerFeatures();
    };

    map.on("sourcedata", handleData);
    return () => {
      map.off("sourcedata", handleData);
      readLayerFeatures.cancel();
    };
  }, [map, versionConfig, realLayers, readLayerFeatures]);

  return useMemo(
    () => (versionConfig ? countLayerConfigFeatures(layerFeatures, { layerConfigs, versionConfig, selectionGeometry }) : {}),
    [layerFeatures, layerConfigs, versionConfig, selectionGeometry]
  );
};

export default useMapboxFeatureTotals;
